import { useState, useEffect, useRef } from "react";
import { FaTimes, FaImage, FaSave, FaEdit, FaUpload, FaLink, FaTrash, FaSpinner } from "react-icons/fa";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useAuth } from './AuthContext';
import "./BlogFormModal.css";

// Quill toolbar modules configuration
const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ],
};

const formats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'check',
  'indent', 'align', 'blockquote', 'code-block',
  'link', 'image', 'video'
];

function BlogFormModal({ isOpen, onClose, editBlog, onSubmit }) {
  const { user, token } = useAuth();
  const [heading, setHeading] = useState("");
  const [image, setImage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageInputType, setImageInputType] = useState("url");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (editBlog) {
        console.log("Editing blog:", editBlog);
        setHeading(editBlog.heading || "");
        setImage(editBlog.image || "");
        setImagePreview(editBlog.image || "");
        // IMPORTANT: Set content from editBlog
        setContent(editBlog.content || "");
      } else {
        resetForm();
      }
    }
  }, [isOpen, editBlog]);

  const resetForm = () => {
    setHeading("");
    setImage("");
    setImageFile(null);
    setImagePreview("");
    setContent("");
    setError("");
  };

  if (!isOpen) return null;

  // Upload image to server
  const uploadImageToServer = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch('https://airexheater.in/api/admin/upload/blog-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        return { success: true, url: data.url };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: 'Upload failed' };
    }
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should be less than 5MB");
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }
    
    setUploading(true);
    setError("");
    
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Upload to server
    const result = await uploadImageToServer(file);
    
    if (result.success) {
      setImage(result.url);
      setImageFile(null);
      setError("");
    } else {
      setError(result.error || 'Failed to upload image');
      setImagePreview("");
    }
    
    setUploading(false);
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImage(url);
    setImagePreview(url);
    setImageFile(null);
    setError("");
  };

  const handleRemoveImage = () => {
    setImage("");
    setImagePreview("");
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!heading.trim()) {
      setError("Blog heading is required");
      return;
    }
    if (!image.trim()) {
      setError("Image is required");
      return;
    }
    
    // Check if content is empty (Quill returns <p><br></p> for empty content)
    const isEmptyContent = !content || content === '<p><br></p>' || content === '<p></p>' || content === '<p><br></p>' || content === '';
    
    if (isEmptyContent) {
      setError("Blog content is required");
      return;
    }
    
    setLoading(true);
    setError("");
    
    const result = await onSubmit({
      heading: heading.trim(),
      image: image.trim(),
      content: content,
    });
    
    setLoading(false);
    
    if (result.success) {
      resetForm();
      onClose();
    } else {
      setError(result.error || "Failed to save blog");
    }
  };

  return (
    <div className="blog-modal-overlay" onClick={onClose}>
      <div className="blog-modal" onClick={(e) => e.stopPropagation()}>
        <div className="blog-modal-header">
          <h2>
            {editBlog ? <FaEdit /> : <FaSave />}
            {editBlog ? " Edit Blog" : " Add New Blog"}
          </h2>
          <button className="blog-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="blog-form">
          {error && <div className="blog-error">{error}</div>}
          
          <div className="blog-form-group">
            <label>Blog Heading *</label>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              placeholder="Enter blog title..."
              required
            />
          </div>

          <div className="blog-form-group">
            <label>Image Source</label>
            <div className="image-source-toggle">
              <button 
                type="button"
                className={`source-btn ${imageInputType === 'url' ? 'active' : ''}`}
                onClick={() => setImageInputType('url')}
              >
                <FaLink /> Image URL
              </button>
              <button 
                type="button"
                className={`source-btn ${imageInputType === 'upload' ? 'active' : ''}`}
                onClick={() => setImageInputType('upload')}
              >
                <FaUpload /> Upload Image
              </button>
            </div>
          </div>

          <div className="blog-form-group">
            {imageInputType === 'url' ? (
              <>
                <label>Image URL *</label>
                <div className="blog-image-input">
                  <FaImage className="blog-image-icon" />
                  <input
                    type="url"
                    value={image}
                    onChange={handleImageUrlChange}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </>
            ) : (
              <>
                <label>Upload Image *</label>
                <div className="blog-upload-area">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageFileChange}
                    style={{ display: 'none' }}
                    id="blog-image-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="blog-image-upload" className="upload-label">
                    {uploading ? <FaSpinner className="spin" /> : <FaUpload />} 
                    {uploading ? ' Uploading...' : ' Choose Image'}
                  </label>
                  <p className="upload-hint">Max file size: 5MB (JPG, PNG, GIF, WEBP)</p>
                </div>
              </>
            )}
            
            {imagePreview && (
              <div className="blog-image-preview">
                <img src={imagePreview} alt="Preview" />
                <button type="button" className="remove-image-btn" onClick={handleRemoveImage} disabled={uploading}>
                  <FaTrash /> Remove
                </button>
              </div>
            )}
          </div>

          <div className="blog-form-group">
            <label>Blog Content *</label>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              formats={formats}
              placeholder="Write your blog content here..."
              style={{ height: '400px', marginBottom: '60px' }}
            />
            <small className="content-hint">💡 Tip: Use headings (H1, H2, H3) to structure your content</small>
          </div>

          <div className="blog-form-actions">
            <button type="button" className="blog-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="blog-submit-btn" disabled={loading || uploading}>
              {loading ? "Saving..." : (editBlog ? "Update Blog" : "Add Blog")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BlogFormModal;